/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import * as Yup from 'yup';

import EFormValidation from '@/main/modules/form/const/formValidation';
import { IPageNewsTranslation } from '@/main/modules/internationalization/types';
import { ICorporateFormTranslation } from '@/main/modules/internationalization/types/corporate';
import { getFileExtension } from '@/main/utils/formatText';
import { stringFormatParams } from '@/main/utils/stringFormat';

import { ENewsFormCreationFields, MAX_IMAGES, MAX_LINK_LENGTH, MAX_TITLE_LENGTH, VALID_EXTENSIONS } from './constants';

const getNewsBaseFormValidationSchema = (translation: ICorporateFormTranslation) => {
    return Yup.object().shape({
        [ENewsFormCreationFields.TITLE]: Yup.string()
            .max(
                MAX_TITLE_LENGTH,
                stringFormatParams(translation.validation['field.title.long'], { number: MAX_TITLE_LENGTH }),
            )
            .required(translation.validation['field.required']),
        [ENewsFormCreationFields.PUBLISH_FROM]: Yup.string().required(translation.requiredField),
        [ENewsFormCreationFields.PUBLISH_TO]: Yup.string(),
    });
};

export const getNewsExternalFormValidationSchema = (translation: ICorporateFormTranslation) => {
    const baseValidationSchema = getNewsBaseFormValidationSchema(translation);

    return baseValidationSchema.concat(
        Yup.object().shape({
            [ENewsFormCreationFields.LINK]: Yup.string()
                .required(translation.validation['field.required'])
                .url(translation.validation['field.link.incorrect'])
                .max(
                    MAX_LINK_LENGTH,
                    stringFormatParams(translation.validation['field.link.long'], { number: MAX_LINK_LENGTH }),
                ),
        }),
    );
};

export const getNewsInternalFormValidationSchema = (translation: ICorporateFormTranslation) => {
    const baseValidationSchema = getNewsBaseFormValidationSchema(translation);

    return baseValidationSchema.concat(
        Yup.object().shape({
            [ENewsFormCreationFields.MARKDOWN]: Yup.string().required(translation.validation['field.required']),
            [ENewsFormCreationFields.IMAGES]: Yup.array(
                Yup.string().url(translation.validation['field.link.incorrect']),
            ).max(MAX_IMAGES, stringFormatParams(translation.validation['field.number.max'], { number: MAX_IMAGES })),
            [ENewsFormCreationFields.REPEAT]: Yup.number()
                .integer(translation.validation['field.number.integer'])
                .min(0, translation.validation['field.number.nonnegative'])
                .test('isNumber', translation.validation['field.number.notnumber'], (value) => {
                    return !isNaN(Number(value));
                }),
            [ENewsFormCreationFields.USER_FILE]: Yup.mixed().test(
                'file extension',
                stringFormatParams(translation.validation['file.format.incorrect'], {
                    format: VALID_EXTENSIONS.join(', ').toUpperCase(),
                }),
                (value) => {
                    if (!value) return true;
                    const ext = getFileExtension((value as File).name);
                    return VALID_EXTENSIONS.includes(ext);
                },
            ),
        }),
    );
};

export const validatePublishError = (
    tCorporate: ICorporateFormTranslation,
    tNews: IPageNewsTranslation,
    error?: string,
) => {
    if (!error) return null;
    if (error === EFormValidation.fileFormatIncorrect)
        return stringFormatParams(tCorporate.validation['file.format.incorrect'], {
            format: VALID_EXTENSIONS.join(', ').toUpperCase(),
        });
    if (tCorporate.validation[error as EFormValidation]) return tCorporate.validation[error as EFormValidation];
    return tNews.modals.publishModals.errors[error];
};
